/**
 * @fileoverview Drills panel — the 4C/ID "part-task practice" component.
 *
 * Two short, repeatable exercises decoupled from the live simulation:
 *  1. Design-flow & preset calculator (the 30-second field calculation)
 *  2. Diagnosis flashcards (symptom → cause)
 *
 * @module HydraulicBalancingTesting/components/DrillsPanel
 */

import React, { useState, useCallback } from 'react';
import { Button, InputNumber, Select, Alert, Tag, Segmented, Space } from 'antd';
import { ThunderboltOutlined, ReloadOutlined } from '@ant-design/icons';

import { LatexRenderer } from '../../../Shared/LatexRenderer';
import { PRESET_POSITIONS } from '../constants';
import {
  generatePresetDrill,
  checkFlowAnswer,
  checkPresetAnswer,
  FLASHCARDS,
} from '../content';

import styles from '../HydraulicBalancingTesting.module.css';

// ----------------------------------------------------------------------------
// Drill 1: design flow & preset calculator
// ----------------------------------------------------------------------------

const PresetDrill = () => {
  const [drill, setDrill] = useState(generatePresetDrill);
  const [flowAnswer, setFlowAnswer] = useState(null);
  const [flowResult, setFlowResult] = useState(null); // null | true | false
  const [presetAnswer, setPresetAnswer] = useState(null);
  const [presetResult, setPresetResult] = useState(null);
  const [score, setScore] = useState({ done: 0, perfect: 0 });

  const nextRound = useCallback(() => {
    setDrill(generatePresetDrill());
    setFlowAnswer(null);
    setFlowResult(null);
    setPresetAnswer(null);
    setPresetResult(null);
  }, []);

  const finishRound = (presetOk) => {
    setScore((s) => ({
      done: s.done + 1,
      perfect: s.perfect + (presetOk && flowResult ? 1 : 0),
    }));
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        message="The 30-second field calculation"
        description={
          <span>
            Verfahren B in miniature: from a room's heat load, compute the design flow, then pick
            the preset. With a 5 K spread: <LatexRenderer latex="$ \dot{V}\,[l/h] = \dot{Q}\,[W] / 5.8 $" />
            {' '}and the preset must give this flow at the available pressure:{' '}
            <LatexRenderer latex="$ K_v = \dot{V}\,[m^3/h] / \sqrt{\Delta p\,[bar]} $" />
          </span>
        }
        style={{ marginBottom: 12 }}
      />

      <div className={styles.drillGiven}>
        <Tag color="blue">Given</Tag>
        <span>Room heat load <b>{drill.load} W</b></span>
        <span>· Design spread <b>5 K</b></span>
        <span>· Available branch Δp <b>{drill.dpKPa} kPa</b> (= {(drill.dpKPa / 100).toFixed(2)} bar)</span>
      </div>

      {/* Step 1: design flow */}
      <p className={styles.sectionLabel}>Step 1 — design flow</p>
      <Space>
        <InputNumber
          placeholder="l/h"
          value={flowAnswer}
          min={0}
          max={2000}
          onChange={setFlowAnswer}
          addonAfter="l/h"
          style={{ width: 150 }}
        />
        <Button
          type="primary"
          size="small"
          disabled={flowAnswer === null}
          onClick={() => setFlowResult(checkFlowAnswer(drill, flowAnswer))}
        >
          Check
        </Button>
      </Space>
      {flowResult === true && (
        <Alert type="success" showIcon style={{ marginTop: 6 }} message={`Correct — ${drill.flowLph.toFixed(0)} l/h.`} />
      )}
      {flowResult === false && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 6 }}
          message={`Not within ±5%. Hint: ${drill.load} W ÷ 5.8 ≈ ?`}
        />
      )}

      {/* Step 2: preset */}
      {flowResult && (
        <>
          <p className={styles.sectionLabel}>Step 2 — choose the preset position</p>
          <Space>
            <Select
              style={{ width: 200 }}
              placeholder="preset position"
              value={presetAnswer}
              onChange={setPresetAnswer}
              options={PRESET_POSITIONS.map((p, i) => ({
                value: i,
                label: `pos ${p.label} (Kv ${p.kv.toFixed(2)})`,
              }))}
            />
            <Button
              type="primary"
              size="small"
              disabled={presetAnswer === null}
              onClick={() => {
                const ok = checkPresetAnswer(drill, PRESET_POSITIONS[presetAnswer].kv);
                setPresetResult(ok);
                finishRound(ok);
              }}
            >
              Check
            </Button>
          </Space>
          {presetResult === true && (
            <Alert
              type="success"
              showIcon
              style={{ marginTop: 6 }}
              message="Good choice!"
              description={`Required Kv ≈ ${drill.requiredKv.toFixed(2)} — position ${PRESET_POSITIONS[presetAnswer].label} delivers ${(PRESET_POSITIONS[presetAnswer].kv * Math.sqrt(drill.dpKPa / 100) * 1000).toFixed(0)} l/h at ${drill.dpKPa} kPa.`}
            />
          )}
          {presetResult === false && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 6 }}
              message={`That position gives ${(PRESET_POSITIONS[presetAnswer].kv * Math.sqrt(drill.dpKPa / 100) * 1000).toFixed(0)} l/h — more than ±15% off. Required Kv ≈ ${drill.requiredKv.toFixed(2)}.`}
            />
          )}
        </>
      )}

      <div className={styles.drillFooter}>
        <Button icon={<ReloadOutlined />} size="small" onClick={nextRound}>
          New numbers
        </Button>
        <span className={styles.drillScore}>
          rounds {score.done} · flawless {score.perfect}
        </span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Drill 2: diagnosis flashcards
// ----------------------------------------------------------------------------

const Flashcards = () => {
  const [index, setIndex] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [score, setScore] = useState({ right: 0, total: 0 });

  const card = FLASHCARDS[index % FLASHCARDS.length];
  const answered = chosen !== null;

  const next = () => {
    setIndex((i) => (i + 1) % FLASHCARDS.length);
    setChosen(null);
  };

  return (
    <div>
      <div className={styles.flashSymptom}>
        <Tag color="volcano">Symptom</Tag>
        <p>{card.symptom}</p>
      </div>
      <p className={styles.sectionLabel}>Most likely cause?</p>
      <div className={styles.flashOptions}>
        {card.options.map((opt, i) => {
          let type = 'default';
          if (answered && i === card.correct) type = 'primary';
          return (
            <Button
              key={i}
              type={type}
              danger={answered && i === chosen && i !== card.correct}
              block
              style={{ whiteSpace: 'normal', height: 'auto', textAlign: 'left' }}
              onClick={() => {
                if (answered) return;
                setChosen(i);
                setScore((s) => ({ right: s.right + (i === card.correct ? 1 : 0), total: s.total + 1 }));
              }}
            >
              {opt}
            </Button>
          );
        })}
      </div>
      {answered && (
        <Alert
          type={chosen === card.correct ? 'success' : 'warning'}
          showIcon
          style={{ marginTop: 8 }}
          message={chosen === card.correct ? 'Correct!' : 'Not this time.'}
          description={card.explain}
          action={<Button size="small" onClick={next}>Next card</Button>}
        />
      )}
      <p className={styles.drillScore} style={{ marginTop: 8 }}>
        card {(index % FLASHCARDS.length) + 1}/{FLASHCARDS.length} · {score.right}/{score.total} correct
      </p>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Panel
// ----------------------------------------------------------------------------

export const DrillsPanel = () => {
  const [drill, setDrill] = useState('calc');
  return (
    <div className={styles.panelCard}>
      <Alert
        type="info"
        showIcon
        icon={<ThunderboltOutlined />}
        message="Drills — train the routine parts"
        description="Short repeatable exercises for the skills you need automatically at hand on site. They run independently of the simulation."
        style={{ marginBottom: 12 }}
      />
      <Segmented
        value={drill}
        onChange={setDrill}
        options={[
          { value: 'calc', label: 'Flow & preset calculator' },
          { value: 'cards', label: 'Diagnosis flashcards' },
        ]}
        style={{ marginBottom: 14 }}
      />
      {drill === 'calc' ? <PresetDrill /> : <Flashcards />}
    </div>
  );
};
